import styled, { css } from 'styled-components';

/**
 * Status of an aspect-ratio variant, shared by the Content Studio clip
 * dimensions dropdown and the Creator Studio file dropdown.
 *
 * - `final` — the AR video is published (has a primary video file)
 * - `draft` — the AR video exists but is not yet published
 * - `new` — the AR video doesn't exist yet and will be created on click
 * - `creating` — the AR video is currently being created (Content Studio)
 * - `publishing` — the AR video is currently being published (Content Studio)
 */
export type AspectRatioStatus =
  | 'final'
  | 'draft'
  | 'new'
  | 'creating'
  | 'publishing';

export const STATUS_CONFIG: Record<
  AspectRatioStatus,
  {
    background: string;
    color: string;
    border?: string;
    text: string;
    /**
     * Render as plain colored text rather than a filled pill — the quieter
     * treatment the Downloads tab uses for publish status (`PublishStatusBadge`
     * in `publishStatusUi`). For transient states, where a solid badge shouts
     * for attention it doesn't need (PLAT-10947).
     */
    subtle?: boolean;
  }
> = {
  final: {
    background: 'var(--primary)',
    color: 'var(--background)',
    text: 'FINAL',
  },
  draft: {
    background: 'var(--text-muted)',
    color: 'var(--background)',
    text: 'DRAFT',
  },
  new: {
    background: 'transparent',
    color: 'var(--text-muted)',
    border: '1px dashed var(--text-muted)',
    text: '+ NEW',
  },
  creating: {
    background: 'transparent',
    color: 'var(--border)',
    border: '1px solid var(--border)',
    text: 'CREATING...',
  },
  publishing: {
    background: 'transparent',
    color: 'var(--accent)',
    text: 'Publishing',
    subtle: true,
  },
};

export const StatusBadge = styled.span<{ status: AspectRatioStatus }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0 6px;
  height: 16px;
  border-radius: 3px;
  font-size: 8px;
  font-weight: 700;
  letter-spacing: 0.03em;
  margin-left: auto;
  white-space: nowrap;
  background: ${({ status }) => STATUS_CONFIG[status].background};
  color: ${({ status }) => STATUS_CONFIG[status].color};
  border: ${({ status }) => STATUS_CONFIG[status].border || 'none'};

  /* Plain colored text at the Downloads tab's scale — no fill, no pill padding,
     no condensed micro-caps. Matches PublishStatusBadge so a publish in flight
     reads the same wherever it's shown. */
  ${({ status }) =>
    STATUS_CONFIG[status].subtle &&
    css`
      padding: 0;
      height: auto;
      font-size: var(--text-xs);
      font-weight: var(--font-weight-medium);
      letter-spacing: normal;
      gap: var(--spacing-xs);
    `}
`;

/**
 * In-flight marker beside a `subtle` status. A slow pulse rather than the old
 * spinner: it carries the same "still working" signal with far less motion,
 * and matches the `Pulse` dot in `publishStatusUi`. Only ever rendered for
 * `publishing`.
 */
export const StatusSpinner = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  animation: clip-dim-pulse 1.4s ease-in-out infinite;

  @keyframes clip-dim-pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.3;
    }
  }
`;
